import "./Loader.css";
import cn from "classnames";
import type { ComponentPropsWithoutRef, CSSProperties, FC } from "react";

type LoaderSize = "sm" | "md" | "lg" | number;

type LoaderStyle = CSSProperties & { "--loader-size"?: string };

interface LoaderProps
	extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
	size?: LoaderSize;
	label?: string;
	block?: boolean;
	fullscreen?: boolean;
	/** Use on primary/dark button backgrounds */
	onDark?: boolean;
}

function resolveSize(size: LoaderSize): {
	className?: string;
	style?: LoaderStyle;
} {
	if (typeof size === "number") {
		return { style: { "--loader-size": `${size}px` } };
	}
	return { className: size };
}

export const Loader: FC<LoaderProps> = ({
	size = "md",
	label,
	block = false,
	fullscreen = false,
	onDark = false,
	className,
	style,
	...rest
}) => {
	const resolved = resolveSize(size);
	const spinner = (
		// biome-ignore lint/a11y/useSemanticElements: the loader needs a presentational container for its spinner markup
		<div
			className={cn("moxie-loader", resolved.className, {
				"on-dark": onDark,
			})}
			style={{ ...resolved.style, ...(!block && !label ? style : undefined) }}
			role="status"
			aria-label={label ?? "Loading"}
			{...(!block && !label ? rest : {})}
		>
			<span className="moxie-loader-core" />
		</div>
	);

	if (!block && !label) {
		return spinner;
	}

	return (
		<div
			className={cn("moxie-loader-block", { fullscreen }, className)}
			style={style}
			{...rest}
		>
			<div className="moxie-loader-halo">{spinner}</div>
			{label && <p className="moxie-loader-label">{label}</p>}
		</div>
	);
};
